/**
 * CollecteSection — bloc « Où déposer mes déchets ? » + « Déchetterie » du pôle
 * info administré. Affiché seulement si la commune a saisi des points ou une
 * déchetterie (payload null sinon → masquage auto).
 *
 * - Carte Leaflet (fond IGN) des points, filtrable par type ; légende code
 *   couleur alignée sur le calendrier de collecte.
 * - Fiche déchetterie (horaires, déchets acceptés) si renseignée.
 * - Dossier d'inscription : dépôt + suivi de statut + upload de pièces, calqué
 *   sur le pattern des autres dossiers (urbanisme, concessions).
 */

import { useMemo, useRef, useState } from 'react';
import { MapPin, Recycle, Upload, FileText, X } from 'lucide-react';
import { SectionRepliable } from '@/components/ui/SectionRepliable';
import { Badge, Button } from '@/components/ui';
import { CollecteMap } from '@/components/collecte/CollecteMap';
import { COLLECTE_COLOR, COLLECTE_LABEL } from '@/components/collecte/collecteTypes';
import {
  api, ApiException,
  type CollecteCitoyenPayload, type CollecteType, type CollecteDossier,
} from '@/lib/api';

export function CollecteSection({
  data, onUpdated,
}: {
  data: CollecteCitoyenPayload;
  onUpdated: (next: CollecteCitoyenPayload | null) => void;
}) {
  const [filtre, setFiltre] = useState<CollecteType | 'tous'>('tous');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [pieceLibelle, setPieceLibelle] = useState<string | null>(null);

  const typesPresents = useMemo(() => {
    const s = new Set<CollecteType>();
    data.points.forEach((p) => s.add(p.type));
    return Array.from(s);
  }, [data.points]);

  const pointsAffiches = useMemo(
    () => (filtre === 'tous' ? data.points : data.points.filter((p) => p.type === filtre)),
    [data.points, filtre]
  );

  const dech = data.dechetterie;
  const monDossier = dech?.monDossier ?? null;

  async function refresh() {
    try { onUpdated(await api.collecte.get()); } catch { /* ignore */ }
  }

  async function deposer() {
    setBusy(true); setMsg(null);
    try {
      await api.collecte.deposerDossier({});
      await refresh();
      setMsg('Dossier créé. Ajoutez vos pièces ci-dessous.');
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Erreur lors du dépôt.');
    } finally { setBusy(false); }
  }

  async function envoyerPiece(file: File) {
    if (!monDossier) return;
    setBusy(true); setMsg(null);
    try {
      await api.collecte.ajouterPiece(monDossier.id, file, pieceLibelle ?? undefined);
      await refresh();
      setMsg('Pièce ajoutée.');
      setPieceLibelle(null);
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Erreur lors de l\'envoi.');
    } finally { setBusy(false); }
  }

  async function supprimerPiece(pieceId: string) {
    if (!monDossier) return;
    setBusy(true); setMsg(null);
    try {
      await api.collecte.supprimerPiece(monDossier.id, pieceId);
      await refresh();
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Suppression impossible.');
    } finally { setBusy(false); }
  }

  async function telechargerAttestation(dossierId: string) {
    setBusy(true); setMsg(null);
    try {
      const blob = await api.collecte.attestationBlob(dossierId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'attestation-dechetterie.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Attestation indisponible.');
    } finally { setBusy(false); }
  }

  const statutBadge = (d: CollecteDossier) => {
    switch (d.statut) {
      case 'acceptee': return <Badge variant="success" size="sm">Acceptée</Badge>;
      case 'refusee': return <Badge variant="urgent" size="sm">Refusée</Badge>;
      case 'en_cours': return <Badge variant="info" size="sm">En cours d'examen</Badge>;
      default: return <Badge variant="warning" size="sm">Reçue</Badge>;
    }
  };

  return (
    <>
      {/* ---- Où déposer : carte + filtres ---- */}
      {data.points.length > 0 && (
        <SectionRepliable
          title="Où déposer mes déchets ?"
          icon={<MapPin className="h-5 w-5 text-grc" aria-hidden="true" />}
          defaultOpen={false}
          right={<Badge variant="success" size="sm">Carte</Badge>}
        >
          <div className="mb-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setFiltre('tous')}
              className={`inline-flex items-center gap-1.5 rounded-r-pill border px-3 py-1 text-xs transition
                ${filtre === 'tous' ? 'border-grc bg-surface text-ink' : 'border-border text-ink-muted hover:text-ink'}`}
            >
              Tous
            </button>
            {typesPresents.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setFiltre(t)}
                className={`inline-flex items-center gap-1.5 rounded-r-pill border px-3 py-1 text-xs transition
                  ${filtre === t ? 'border-grc bg-surface text-ink' : 'border-border text-ink-muted hover:text-ink'}`}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: COLLECTE_COLOR[t] }} aria-hidden="true" />
                {COLLECTE_LABEL[t]}
              </button>
            ))}
          </div>

          <CollecteMap points={pointsAffiches} height={280} />

          <ul className="mt-3 space-y-2">
            {pointsAffiches.map((p) => (
              <li key={p.id} className="rounded-r-lg border border-border bg-surface/50 p-3">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 flex-shrink-0 rounded-full" style={{ background: COLLECTE_COLOR[p.type] }} aria-hidden="true" />
                  <span className="font-medium text-ink">{p.nom}</span>
                  <span className="ml-auto text-xs uppercase tracking-wide" style={{ color: COLLECTE_COLOR[p.type] }}>
                    {COLLECTE_LABEL[p.type]}
                  </span>
                </div>
                {p.adresse && <div className="mt-1 pl-5 text-xs text-ink-dim">{p.adresse}</div>}
                {p.horaires && <div className="pl-5 text-xs text-ink-dim">🕒 {p.horaires}</div>}
                {p.dechetsAcceptes.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5 pl-5">
                    {p.dechetsAcceptes.map((d, i) => (
                      <span key={i} className="rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted">{d}</span>
                    ))}
                  </div>
                )}
                {p.note && <div className="mt-1 pl-5 text-xs text-ink-dim italic">{p.note}</div>}
              </li>
            ))}
          </ul>
        </SectionRepliable>
      )}

      {/* ---- Déchetterie (si renseignée) ---- */}
      {dech && (
        <SectionRepliable
          title="Déchetterie"
          icon={<Recycle className="h-5 w-5 text-grc" aria-hidden="true" />}
          defaultOpen={false}
          right={dech.inscriptionActive
            ? <Badge variant="info" size="sm">Inscription</Badge>
            : <Badge variant="neutral" size="sm">Infos</Badge>}
        >
          <div className="rounded-r-lg border border-border bg-surface/50 p-3">
            <div className="font-medium text-ink">{dech.nom}</div>
            {dech.adresse && <div className="text-sm text-ink-muted">{dech.adresse}</div>}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-dim">
              {dech.horaires && <span>🕒 {dech.horaires}</span>}
              {dech.latitude !== null && dech.longitude !== null && (
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Localisée sur la carte</span>
              )}
            </div>
            {dech.dechetsAcceptes.length > 0 && (
              <>
                <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-ink-dim">Déchets acceptés</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {dech.dechetsAcceptes.map((d, i) => (
                    <span key={i} className="rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted">{d}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Dossier d'inscription */}
          {dech.inscriptionActive && (
            <div className="mt-3 rounded-r-lg border border-info/40 bg-info-tint/20 p-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-ink">Obtenir ma carte d'accès</span>
                {monDossier ? statutBadge(monDossier) : <Badge variant="warning" size="sm">À compléter</Badge>}
              </div>
              {dech.inscriptionIntro && <p className="mt-1 text-sm text-ink-muted">{dech.inscriptionIntro}</p>}

              {/* Pièces attendues */}
              {dech.piecesAttendues.length > 0 && (
                <ul className="mt-2.5 space-y-1.5">
                  {dech.piecesAttendues.map((pa) => {
                    const fournie = monDossier?.pieces.some((pj) => pj.libelle === pa.libelle);
                    return (
                      <li key={pa.id} className="flex items-center gap-2 rounded-r-md border border-border bg-bg px-2.5 py-2 text-sm">
                        <span className={`h-4 w-4 flex-shrink-0 rounded-r-sm border ${fournie ? 'border-success bg-success' : 'border-border-hi'}`} aria-hidden="true" />
                        <span className={fournie ? 'text-ink' : 'text-ink-muted'}>{pa.libelle}</span>
                        {pa.obligatoire && <span className="ml-auto text-xs text-ink-dim">obligatoire</span>}
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Décision délivrée */}
              {monDossier?.statut === 'acceptee' && monDossier.numeroCarte && (
                <div className="mt-2.5 rounded-r-md border border-success/40 bg-success-tint/20 px-3 py-2 text-sm text-ink">
                  Carte accordée — numéro : <span className="font-semibold">{monDossier.numeroCarte}</span>
                  {monDossier.attestationDisponible && (
                    <div className="mt-2">
                      <Button variant="primary" size="sm" onClick={() => telechargerAttestation(monDossier.id)} disabled={busy}>
                        <FileText className="h-4 w-4" /> Télécharger mon attestation (PDF)
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {monDossier?.statut === 'refusee' && monDossier.reponseStaff && (
                <div className="mt-2.5 rounded-r-md border border-urgent/40 bg-urgent-tint/20 px-3 py-2 text-sm text-ink">
                  Refusé — {monDossier.reponseStaff}
                </div>
              )}

              {/* Actions */}
              <div className="mt-3 flex flex-wrap gap-2">
                {dech.aFormulaire && (
                  <Button
                    variant="ghost" size="sm"
                    onClick={async () => {
                      try { const { url } = await api.grc.collecte.getFormulaireUrl(); window.open(url, '_blank'); }
                      catch { setMsg('Formulaire indisponible.'); }
                    }}
                  >
                    <FileText className="h-4 w-4" /> Formulaire
                  </Button>
                )}
                {!monDossier && (
                  <Button variant="primary" size="sm" onClick={deposer} disabled={busy}>
                    Démarrer ma demande
                  </Button>
                )}
                {monDossier && ['nouvelle', 'en_cours'].includes(monDossier.statut) && (
                  monDossier.pieces.length >= 2 ? (
                    <p className="text-xs text-ink-dim">2 pièces maximum. Supprimez-en une pour en changer.</p>
                  ) : (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="application/pdf,image/*"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) envoyerPiece(f); e.target.value = ''; }}
                    />
                    {dech.piecesAttendues.length > 0 && (
                      <select
                        value={pieceLibelle ?? ''}
                        onChange={(e) => setPieceLibelle(e.target.value || null)}
                        className="rounded-r-md border border-border bg-bg px-2 py-1.5 text-xs text-ink"
                      >
                        <option value="">Pièce…</option>
                        {dech.piecesAttendues.map((pa) => <option key={pa.id} value={pa.libelle}>{pa.libelle}</option>)}
                      </select>
                    )}
                    <Button variant="primary" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>
                      <Upload className="h-4 w-4" /> Ajouter une pièce
                    </Button>
                  </>
                  )
                )}
              </div>

              {/* Pièces déjà déposées */}
              {monDossier && monDossier.pieces.length > 0 && (
                <ul className="mt-2.5 space-y-1">
                  {monDossier.pieces.map((pj) => (
                    <li key={pj.id} className="flex items-center gap-2 text-xs text-ink-muted">
                      <FileText className="h-3.5 w-3.5" />
                      {pj.url
                        ? <a href={pj.url} target="_blank" rel="noreferrer" className="hover:text-ink underline">{pj.libelle ?? pj.nomFichier ?? 'pièce'}</a>
                        : (pj.libelle ?? pj.nomFichier ?? 'pièce')}
                      {['nouvelle', 'en_cours'].includes(monDossier.statut) && (
                        <button type="button" onClick={() => supprimerPiece(pj.id)} disabled={busy}
                          className="ml-auto text-ink-dim hover:text-urgent" title="Supprimer cette pièce">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {msg && <p className="mt-2 text-xs text-ink-dim">{msg}</p>}
            </div>
          )}
        </SectionRepliable>
      )}
    </>
  );
}
